import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { BorrowingBaseReportGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { VerificationFundingRequestComponent } from './VerificationFundingRequestComponent';
import {
  isVerificationReportFundingRequestComponentTypes,
  VerificationReportFundingRequestComponentTypes,
} from './VerificationFundingRequestTableComponents';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import {
  isSanitizedJSON,
  isSanitizedJSONWithNumberValues,
  VerificationApproveTablesMode,
} from './verificationTablesUtils';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetCurrentFundingRequestConstraintForCreditFacilityQuery } from 'packages/funding-requests/requests/requests.gql.generated';

type VerificationFundingRequestTableProps = {
  verificationReport: Util.SanitizedJSON;
  verificationReportFinleyAgentBorrowingBaseReport: BorrowingBaseReportGQLType;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  viewMode: VerificationApproveTablesMode;
};
export const VerificationFundingRequestTable = ({
  verificationReport,
  verificationReportFinleyAgentBorrowingBaseReport,
  creditFacilityId,
  reportingDate,
  viewMode,
}: VerificationFundingRequestTableProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { navigator } = useVerificationOptions();

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: 'Error loading credit agreement.',
      });
    },
  });

  const {
    data: fundingRequestConstraintData,
    loading: isFundingRequestConstraintLoading,
  } = useGetCurrentFundingRequestConstraintForCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact ||
    !fundingRequestConstraintData ||
    isFundingRequestConstraintLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="verification-funding-request-table-loading"
      />
    );
  }

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
  } catch (err) {
    toast({
      ...errorToastConfig,
      id: 'errorGetCurrentCreditAgreementFullArtifact',
      title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
    });
    navigator(navigate).reset();
  }

  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );
  const { fundingRequestMechanics } = parsedCreditAgreement;
  const {
    drawRequest,
    collectionsRecycle,
    curePaydown,
    collectionsPaydown,
  } = fundingRequestMechanics;

  const fundingRequestTypeToMechanicsMap: {
    [key in VerificationReportFundingRequestComponentTypes]: CreditAgreement.DomainEntity.DrawRequestProperties &
      CreditAgreement.DomainEntity.CollectionsRecycleProperties &
      CreditAgreement.DomainEntity.CollectionsPaydownProperties &
      CreditAgreement.DomainEntity.CurePaydownProperties;
  } = {
    [VerificationReportFundingRequestComponentTypes.drawRequestTotalAmount]: drawRequest,
    [VerificationReportFundingRequestComponentTypes.collectionsRecycleTotalAmount]: collectionsRecycle,
    [VerificationReportFundingRequestComponentTypes.collectionsPaydownTotalAmount]: collectionsPaydown,
    [VerificationReportFundingRequestComponentTypes.curePaydownTotalAmount]: curePaydown,
  };

  let maxCollectionsRecycleAmount =
    fundingRequestConstraintData.currentFundingRequestConstraint
      ?.collectionsRecycleMax;
  if (fundingRequestConstraintData.currentFundingRequestConstraint) {
    const {
      collectionsRecycleContributesToBorrowingBase,
      maxFundingRequestAmount,
    } = fundingRequestConstraintData.currentFundingRequestConstraint;

    const {
      cashAdvanceRate,
      currentPrincipalBalance,
      totalPreAdvanceCashContribution,
      maxReceivableAdvanceAmount,
    } = verificationReportFinleyAgentBorrowingBaseReport;

    // take into consideration recycling cash that is proping up the borrowing base
    if (
      maxFundingRequestAmount >= 0 &&
      !collectionsRecycleContributesToBorrowingBase &&
      totalPreAdvanceCashContribution
    ) {
      const borrowingBaseRecyclingCash =
        maxReceivableAdvanceAmount -
        currentPrincipalBalance +
        totalPreAdvanceCashContribution * cashAdvanceRate;

      maxCollectionsRecycleAmount = Math.min(
        fundingRequestConstraintData.currentFundingRequestConstraint
          ?.collectionsRecycleMax,
        borrowingBaseRecyclingCash,
      );
    }
  }

  // each of these max constraints may or may not exist
  const fundingRequestTypeToMaximumConstraintAmount: {
    [key in VerificationReportFundingRequestComponentTypes]: Util.Maybe<number>;
  } = {
    [VerificationReportFundingRequestComponentTypes.drawRequestTotalAmount]:
      fundingRequestConstraintData.currentFundingRequestConstraint
        ?.maxDrawRequestAmount, // maxDrawRequestAmount will be null if the maximum is infinity, due to resolver translation for GQL
    [VerificationReportFundingRequestComponentTypes.collectionsRecycleTotalAmount]: maxCollectionsRecycleAmount,
    [VerificationReportFundingRequestComponentTypes.collectionsPaydownTotalAmount]:
      fundingRequestConstraintData.currentFundingRequestConstraint
        ?.collectionsPaydownMax,
    [VerificationReportFundingRequestComponentTypes.curePaydownTotalAmount]: undefined, // we do not currently calculate a cure paydown max constraint
  };

  const { borrowerFundingRequestComponentTotalAmounts } = verificationReport;

  return (
    <Box p="0 16px">
      {isSanitizedJSON(borrowerFundingRequestComponentTotalAmounts) && (
        <Box>
          {_.compact(
            _.map(
              borrowerFundingRequestComponentTotalAmounts,
              (componentTotalAmounts, componentType) => {
                if (
                  isSanitizedJSONWithNumberValues(componentTotalAmounts) &&
                  componentTotalAmounts.borrowerValue &&
                  isVerificationReportFundingRequestComponentTypes(
                    componentType,
                  )
                ) {
                  return (
                    <VerificationFundingRequestComponent
                      key={`verification-funding-request-component-${componentType}`}
                      fundingRequestComponentType={componentType}
                      fundingRequestComponentMechanics={
                        fundingRequestTypeToMechanicsMap[componentType]
                      }
                      fundingRequestComponentTotalAmounts={
                        componentTotalAmounts
                      }
                      fundingRequestComponentMaximumConstraintAmount={
                        fundingRequestTypeToMaximumConstraintAmount[
                          componentType
                        ]
                      }
                      reportingDate={reportingDate}
                      creditFacilityId={creditFacilityId}
                      viewMode={viewMode}
                    />
                  );
                }
                return null;
              },
            ),
          )}
        </Box>
      )}
    </Box>
  );
};
